import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn } from 'lucide-react';

export default function Login() {
  // Pre-fill the email from a ?email= link so the admin only types their password.
  // Stays editable if they need to change it.
  const [email, setEmail] = useState(() => new URLSearchParams(window.location.search).get('email') || '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  // Real login against the CRM. This used to be a demo shortcut that accepted one
  // hardcoded pair and signed in by POSTing hardcoded admin credentials — which
  // shipped those credentials inside the JavaScript bundle for anyone to read, and
  // meant a genuine admin could not sign in with their own account at all.
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await fetch('/crm-api/auth/login', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });
      const d = await r.json();
      if (!d.success) { setError(d.error || 'Invalid credentials'); return; }
      if (d.user.role !== 'admin') {
        setError('This login is for admins. Staff sign in at /employee/login');
        return;
      }
      localStorage.setItem('token', d.token);          // legacy key, still read in places
      localStorage.setItem('crm_token', d.token);
      localStorage.setItem('crm_user', JSON.stringify(d.user));
      navigate('/dashboard');
    } catch {
      setError('Cannot reach the server. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0e3b2a] via-[#0b6e3e] to-[#08150f] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-24 h-24 bg-white rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl p-2">
            <img src="/dfci-logo.png" alt="Diabetik Foot Care India" className="max-w-full max-h-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-1">
            Diabetik Foot Care India
          </h1>
          <p className="text-emerald-100">Admin Dashboard Login</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleLogin} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="you@diabetikfootcare.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Enter your password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              className="w-full bg-gradient-to-r from-[#1a8046] to-[#0b6e3e] text-white py-3 rounded-lg font-semibold hover:from-[#156637] hover:to-[#0a5c34] transition-all disabled:opacity-60"
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <a href="/forgot-password" className="text-sm text-teal-600 hover:text-teal-700">
              Forgot password?
            </a>
          </div>

          <div className="mt-4 text-center">
            <p className="text-sm text-gray-500">
              Staff sign in at <a href="/employee/login" className="text-[#0b6e3e] font-semibold">/employee/login</a>
            </p>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-emerald-100 text-sm">
            © 2026 Diabetik Foot Care India Pvt. Ltd.
          </p>
        </div>
      </div>
    </div>
  );
}